import * as React from 'react';

import { getNextIndex, getPreviousIndex } from '~shared/utils/get-index';

import tabA11y from './a11y';
import {
  Action,
  TAB_BUTTON_CLICK,
  TAB_BUTTON_END,
  TAB_BUTTON_ENTER,
  TAB_BUTTON_HOME,
  TAB_BUTTON_NEXT,
  TAB_BUTTON_PREV,
  TAB_BUTTON_SPACE,
  TAB_BUTTON_TAB,
  TAB_LIST_FOCUSED,
  TAB_LIST_UPDATED,
  TAB_PANELS_UPDATED,
} from './actions';

interface State {
  selectedIndex: number;
  focusedTabIndex: number | null;
  tabItems: Array<string | number>;
  panels: number[];
}

interface TabsContextValues extends State {
  dispatch: (action: Action) => void;
  a11y: ReturnType<typeof tabA11y>;
  onChange?: (selectedIndex: number, selectedItem?: string) => void;
}

export const TabsContext = React.createContext<TabsContextValues | null>(null);

export type TabsProviderProps<T extends string> = {
  children: React.ReactNode;
  id: string;
  selectedItem?: T;
  onChange?: (selectedIndex: number, T?: string) => void;
};

/**
 * @deprecated Use the TabsV2 components instead.
 */
export const TabsProvider = <T extends string>({
  children,
  onChange,
  id,
  selectedItem,
}: TabsProviderProps<T>) => {
  const [tabsState, dispatch] = React.useReducer(
    (state: State, action: Action): State => {
      switch (action.type) {
        case TAB_BUTTON_PREV: {
          return {
            ...state,
            focusedTabIndex: getPreviousIndex(
              state.focusedTabIndex,
              state.tabItems.length
            ),
          };
        }
        case TAB_BUTTON_NEXT: {
          return {
            ...state,
            focusedTabIndex: getNextIndex(
              state.focusedTabIndex,
              state.tabItems.length
            ),
          };
        }
        case TAB_BUTTON_HOME: {
          return {
            ...state,
            focusedTabIndex: 0,
          };
        }
        case TAB_BUTTON_END: {
          return {
            ...state,
            focusedTabIndex: state.tabItems.length - 1,
          };
        }
        case TAB_BUTTON_TAB: {
          return {
            ...state,
            focusedTabIndex: null,
          };
        }
        case TAB_BUTTON_ENTER:
        case TAB_BUTTON_SPACE:
        case TAB_BUTTON_CLICK: {
          return {
            ...state,
            focusedTabIndex: action.value,
            selectedIndex: action.value,
          };
        }
        case TAB_LIST_FOCUSED: {
          return {
            ...state,
            focusedTabIndex: action.value || 0,
          };
        }
        case TAB_LIST_UPDATED: {
          return {
            ...state,
            tabItems: action.tabItems,
            selectedIndex: 0,
          };
        }
        case TAB_PANELS_UPDATED: {
          return {
            ...state,
            panels: action.panels,
          };
        }

        default:
          return state;
      }
    },
    {
      selectedIndex: -1,
      focusedTabIndex: null,
      tabItems: [],
      panels: [],
    }
  );

  return (
    <TabsContext.Provider
      value={{
        ...tabsState,
        selectedIndex: selectedItem
          ? tabsState.tabItems.indexOf(selectedItem)
          : tabsState.selectedIndex,
        dispatch,
        a11y: tabA11y({ uniqueId: id }),
        onChange,
      }}
    >
      {children}
    </TabsContext.Provider>
  );
};
